import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Tooltip as MuiTooltip } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  AlertTriangle,
  ArrowRightLeft,
  BadgePercent,
  Building2,
  Check,
  ChevronDown,
  Copy,
  FileCheck2,
  Landmark,
  ListOrdered,
  Percent,
  PiggyBank,
  Plus,
  Target,
  Trash2,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { createPortal } from "react-dom";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useFormContext,
  useWatch,
  type Control,
  type Path,
} from "react-hook-form";

import type { BuyOrderRow } from "../orderForm/components/BuyOrders";
import type { SellOrderRow } from "../orderForm/components/SellOrders";
import { useOrderForm } from "../orderForm/hooks/useOrderForm";
import {
  CAPGUARD_PORT_TYPES,
  COMPANY_ALLOWED_PORT_TYPES,
  getManagementFeeOptions,
  getModelOptions,
  portfolioTypeOptions,
} from "../orderForm/OrderForm.options";
import { validateAccountNumber } from "../orderForm/OrderForm.rules";
import { OrderFormData } from "../orderForm/OrderForm.schema";
import { QueryModelPosition, QuerySecurity } from "@/api/queries/types";
import { Portfolio, PortfolioReportItem } from "@/api/types";
import { PRICELIST_ROWS } from "@/components/Pricelist/Pricelist";
import { Switch } from "@/components/switch";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/number";

// Ground-up redesign: only the shared schema/hook/business-rules from
// ../orderForm are reused here — every visual component below is new.
dayjs.extend(customParseFormat);

// ─── Business rules (kept functionally identical to the legacy form) ─────────

const NON_STOCK_CLASS1 = new Set(["L", "ALT", "R", "K", "PF"]);
function isEquityByClass1(code: string): boolean {
  return !!code && !NON_STOCK_CLASS1.has(code);
}

const NON_STOCK_MODEL_CLASS1 = new Set(["1", "12"]);
function isEquityByModelClass1(code: string): boolean {
  return !NON_STOCK_MODEL_CLASS1.has(String(code ?? ""));
}

function getModelEquityFractionFallback(modelType: string): number {
  if (!modelType || modelType === "NOMODEL") return 0;
  if (modelType.includes("RENTER")) return 0;
  if (modelType.includes("TRYGG")) return 0.25;
  if (
    modelType.includes("EIENDOM") ||
    modelType.includes("EKB") ||
    modelType.includes("AKSJER")
  )
    return 1;
  const match = modelType.match(/(\d+)/);
  return match ? Math.min(Number(match[1]), 100) / 100 : 0;
}

function getMinimumMandateAmount(modelType: string | undefined): number {
  if (modelType === "CAPGUARDEKB") return 1_000_000;
  if (modelType === "CAPGUARDEIENDOM") return 750_000;
  return 0;
}

const MODEL_DISPLAY_NAMES: Record<string, string> = {
  CAPGUARDRENTER: "Aktiv Renter",
  CAPGUARDTRYGG: "Trygg",
  CAPGUARDEKB: "Aktiv EKB",
  CAPGUARDEIENDOM: "Aktiv Eiendom",
  FONDSOBLIGASJON: "Fondsobligasjoner",
  AKSJER: "Aktiv Aksjer",
  CAPGUARDAKSJER: "Aktiv Aksjer",
  CAPGUARD75: "Aktiv 75/25",
  CAPGUARD60: "Aktiv 60/40",
  CAPGUARD45: "Aktiv 45/55",
  CAPGUARD25: "Aktiv 25/75",
  NORDNET: "Nordnet",
};

const MAX_INSTRUMENT_ROWS = 6;
const MAX_SAVINGS_FUNDS = 5;

// ─── Small formatting helpers ──────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString("nb-NO", { maximumFractionDigits: 0 });
}

function normalizeAccountNumber(value: string | undefined): string {
  return (value ?? "").replace(/\D/g, "").slice(0, 11);
}

function formatAccountNumber(value: string | undefined): string {
  const digits = normalizeAccountNumber(value);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  return `${digits.slice(0, 4)} ${digits.slice(4, 6)} ${digits.slice(6)}`;
}

const VALID_ACCOUNT_TOOLTIP =
  "Kontrollsifferet er gyldig. Dette er ikke sjekket mot KAR-registeret eller andre eksterne registre.";

function isoToNorwegian(iso: string): string {
  const parsed = dayjs(iso, "YYYY-MM-DD");
  return parsed.isValid() ? parsed.format("DD.MM.YYYY") : "";
}

function norwegianToIso(value: string): string {
  const parsed = dayjs(value, "DD.MM.YYYY");
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

function fieldPath(name: string) {
  return name as Path<OrderFormData>;
}

// The schema's `any[]` buyOrders/sellOrders leak into OrderFormData's array-path
// inference, so useFieldArray can't resolve dpSavingsFunds's item type from it.
type SavingsFundsFieldValues = {
  dpSavingsFunds: { name: string; pct: number }[];
};

// react-hook-form types nested/array field errors as FieldError | Merge<...>; only ever render plain strings.
function errMsg(error?: { message?: unknown }): string | undefined {
  return typeof error?.message === "string" ? error.message : undefined;
}

function makeEmptyBuyRow(): BuyOrderRow {
  return {
    id: Math.random().toString(36).slice(2),
    securityId: null,
    instrumentCode: "",
    instrumentName: "",
    isinCode: "",
    class1code: "",
    class2id: "",
    class2code: "",
    type: "",
    limitPrice: "",
    holding: "",
    feePercent: "",
    price: "",
    fx: "1",
    quantity: "",
    amountInput: "",
    duration: "",
  };
}

function makeSellRowFromHolding(item: PortfolioReportItem): SellOrderRow {
  return {
    securityCode: item.security.securityCode,
    securityId: item.security.id,
    name: item.security.name,
    class2code: item.security.classType2?.code ?? "",
    holding: item.amount,
    marketUnitPrice: item.marketUnitPrice,
    sellQty: "",
    feePercent: "",
    type: "",
    limitPrice: "",
    duration: "",
  };
}

// Preserves any already-entered sell details when the holdings list is refreshed.
function mergeSellRows(
  fresh: SellOrderRow[],
  saved: SellOrderRow[]
): SellOrderRow[] {
  if (!saved.length) return fresh;
  const savedMap = new Map(saved.map((r) => [r.securityId, r]));
  return fresh.map((row) => {
    const s = savedMap.get(row.securityId);
    if (!s) return row;
    return {
      ...row,
      sellQty: s.sellQty,
      feePercent: s.feePercent,
      type: s.type,
      limitPrice: s.limitPrice,
      duration: s.duration,
    };
  });
}

// ─── Shared style tokens ───────────────────────────────────────────────────────

const inputCls =
  "w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] text-foreground transition-colors placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15";
const readonlyCls =
  "w-full cursor-default rounded-lg border border-transparent bg-muted/50 px-2.5 py-1.5 text-[13px] text-muted-foreground";
const errorCls = "mt-1 text-[11px] font-medium text-destructive";

// ─── Formatted-amount hook (thousands separator, focus-aware) ─────────────────

type NumericFieldName =
  | "dpAmountToInvest"
  | "dpOnOffAmount"
  | "dpWithdrawal"
  | "dpSavingsAmount"
  | "dpWdAmount";

function useFormattedAmount(name: NumericFieldName) {
  const { setValue } = useFormContext<OrderFormData>();
  const watched = useWatch({ name }) as number | undefined;
  const raw = watched ?? 0;
  const [focused, setFocused] = useState(false);
  const [display, setDisplay] = useState("");

  useEffect(() => {
    if (!focused) setDisplay(raw ? fmt(raw) : "");
  }, [raw, focused]);

  const apply = (value: number) => {
    setValue(name, value, { shouldValidate: true, shouldDirty: true });
    setFocused(false);
    setDisplay(value ? fmt(value) : "");
  };

  const fieldProps = {
    value: display,
    inputMode: "numeric" as const,
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      setDisplay(raw ? String(raw) : "");
      const el = e.target;
      requestAnimationFrame(() => el.select());
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const cleaned = e.target.value
        .replace(/[\s\u00a0]/g, "")
        .replace(/^0+(?=\d)/, "");
      setDisplay(cleaned);
      const num = cleaned === "" ? 0 : Number(cleaned);
      if (!isNaN(num)) setValue(name, num, { shouldValidate: true });
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      const cleaned = e.target.value.replace(/[\s\u00a0]/g, "");
      const num = cleaned === "" ? 0 : Number(cleaned);
      if (!isNaN(num)) setValue(name, num, { shouldValidate: true });
      setFocused(false);
      setDisplay(num ? fmt(num) : "");
    },
  };

  return { raw, apply, fieldProps };
}

function useFormattedAccountNumber() {
  const { setValue } = useFormContext<OrderFormData>();
  const watched = useWatch({ name: "dpNewAccount" }) as string | undefined;
  const raw = watched ?? "";
  const [focused, setFocused] = useState(false);

  const fieldProps = {
    value: focused ? raw : formatAccountNumber(raw),
    inputMode: "numeric" as const,
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      const el = e.target;
      requestAnimationFrame(() => el.select());
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue("dpNewAccount", normalizeAccountNumber(e.target.value), {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    onBlur: () => setFocused(false),
  };

  return { fieldProps };
}

function useBuyOrders() {
  const { setValue } = useFormContext<OrderFormData>();
  const rows = (useWatch({ name: "buyOrders" }) as BuyOrderRow[]) ?? [];
  const update = (next: BuyOrderRow[]) =>
    setValue("buyOrders", next, { shouldValidate: true, shouldDirty: true });
  return { rows, update };
}

function useSellOrders() {
  const { setValue } = useFormContext<OrderFormData>();
  const rows = (useWatch({ name: "sellOrders" }) as SellOrderRow[]) ?? [];
  const update = (next: SellOrderRow[]) =>
    setValue("sellOrders", next, { shouldValidate: true, shouldDirty: true });
  return { rows, update };
}

// ─── Small presentational primitives ───────────────────────────────────────────

function MicroHeader({
  icon: Icon,
  title,
  action,
}: {
  icon: React.ElementType;
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="size-3.5" />
      </span>
      <h3 className="text-[13px] font-semibold tracking-tight text-foreground">
        {title}
      </h3>
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <label className="text-[11px] font-semibold text-foreground/80">
          {label}
        </label>
        {hint}
      </div>
      {children}
      {error && <p className={errorCls}>{error}</p>}
    </div>
  );
}

function StatChip({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-lg px-2.5 py-2",
        highlight ? "bg-primary/10 ring-1 ring-primary/25" : "bg-muted/50"
      )}
    >
      <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          "mt-0.5 text-[13px] font-bold tabular-nums",
          highlight ? "text-primary" : "text-foreground"
        )}
      >
        {value}
      </p>
    </div>
  );
}

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  disabled,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div
      className={cn(
        "inline-flex rounded-lg border border-border bg-muted/40 p-0.5",
        disabled && "opacity-50"
      )}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={cn(
            "rounded-md px-3 py-1 text-[12px] font-semibold transition-colors",
            value === option.value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
            disabled && "cursor-not-allowed"
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ToggleCard({
  icon: Icon,
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  icon: React.ElementType;
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div
      className={cn(
        "flex flex-1 items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors",
        disabled
          ? "border-dashed border-border/60 opacity-50"
          : checked
          ? "border-primary/40 bg-primary/5"
          : "border-border"
      )}
      title={disabled ? "Velg en modellportefølje først" : undefined}
    >
      <span
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-md",
          checked
            ? "bg-primary/15 text-primary"
            : "bg-muted text-muted-foreground"
        )}
      >
        <Icon className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-foreground">{label}</p>
        <p className="truncate text-[10px] text-muted-foreground">{hint}</p>
      </div>
      <Switch
        size="sm"
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
    </div>
  );
}

function AmountField({
  name,
  label,
  error,
  disabled,
  suffix = "kr",
}: {
  name: NumericFieldName;
  label: string;
  error?: string;
  disabled?: boolean;
  suffix?: string;
}) {
  const amount = useFormattedAmount(name);
  return (
    <Field label={label} error={error}>
      <div className="relative">
        <input
          {...amount.fieldProps}
          disabled={disabled}
          placeholder="0"
          className={cn(inputCls, "pr-9", disabled && "opacity-60")}
        />
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] font-medium text-muted-foreground">
          {suffix}
        </span>
      </div>
    </Field>
  );
}

// ─── Portfolio section ─────────────────────────────────────────────────────────

function PortfolioSection({
  portfolios,
  isCompany,
  selected,
  isNew,
  isTransferAccount,
  onJumpToTransfer,
}: {
  portfolios: Portfolio[];
  isCompany: boolean;
  selected?: Portfolio;
  isNew: boolean;
  isTransferAccount: boolean;
  onJumpToTransfer: () => void;
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext<OrderFormData>();
  const portType = useWatch({ name: "dpPortType" }) as string;

  return (
    <div>
      <MicroHeader
        icon={Building2}
        title="Portefølje"
        action={
          isTransferAccount ? (
            <button
              type="button"
              onClick={onJumpToTransfer}
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/10"
            >
              Flyttefullmakt
            </button>
          ) : undefined
        }
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Portefølje" error={errMsg(errors.dpPortfolioId)}>
          <select {...register("dpPortfolioId")} className={inputCls}>
            <option value="">— velg portefølje —</option>
            <option value="NEW">Ny portefølje</option>
            {portfolios.length > 0 && <option disabled>──────────</option>}
            {portfolios
              .filter(
                (p) => !isCompany || COMPANY_ALLOWED_PORT_TYPES.has(p.typeCode)
              )
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.shortName ? `${p.shortName} · ` : ""}
                  {p.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Type" error={errMsg(errors.dpPortType)}>
          {isNew ? (
            <select {...register("dpPortType")} className={inputCls}>
              <option value="">— velg type —</option>
              {portfolioTypeOptions
                .filter(
                  (o) => !isCompany || COMPANY_ALLOWED_PORT_TYPES.has(o.value)
                )
                .map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
            </select>
          ) : (
            <input
              value={
                portfolioTypeOptions.find((o) => o.value === portType)?.label ??
                portType ??
                ""
              }
              readOnly
              className={readonlyCls}
            />
          )}
        </Field>
      </div>

      {selected && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <StatChip
            label="Verdipapirer"
            value={`${fmt(
              selected.portfolioReport?.positionMarketValue ?? 0
            )} kr`}
          />
          <StatChip
            label="Cash"
            value={`${fmt(selected.portfolioReport?.accountBalance ?? 0)} kr`}
          />
          <StatChip
            label="Total"
            value={`${fmt(
              (selected.portfolioReport?.positionMarketValue ?? 0) +
                (selected.portfolioReport?.accountBalance ?? 0)
            )} kr`}
            highlight
          />
        </div>
      )}
    </div>
  );
}

// ─── Strategy & investment section ─────────────────────────────────────────────

const AMOUNT_CHIPS = [
  { label: "10K", value: 10_000 },
  { label: "100K", value: 100_000 },
  { label: "500K", value: 500_000 },
  { label: "1M", value: 1_000_000 },
  { label: "5M", value: 5_000_000 },
];

function StrategySection({
  portType,
  modelOptions,
  showMinWarn,
  minThreshold,
  totalPortValue,
  modelType,
}: {
  portType: string;
  modelOptions: { value: string; label: string }[];
  showMinWarn: boolean;
  minThreshold: number;
  totalPortValue: number;
  modelType: string;
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext<OrderFormData>();
  const amount = useFormattedAmount("dpAmountToInvest");

  return (
    <div>
      <MicroHeader icon={Target} title="Strategi og beløp" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Modellportefølje" error={errMsg(errors.dpModelType)}>
          <select
            {...register("dpModelType")}
            disabled={!portType}
            className={inputCls}
          >
            <option value="">— velg modell —</option>
            {modelOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <div>
          <Field
            label="Beløp å investere"
            error={errMsg(errors.dpAmountToInvest)}
          >
            <div className="relative">
              <input
                {...amount.fieldProps}
                placeholder="0"
                className={cn(inputCls, "pr-9")}
              />
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] font-medium text-muted-foreground">
                kr
              </span>
            </div>
          </Field>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {AMOUNT_CHIPS.map((chip) => (
              <button
                key={chip.label}
                type="button"
                onClick={() => amount.apply(amount.raw + chip.value)}
                className="rounded-md border border-border px-2.5 py-1 text-[10px] font-semibold tabular-nums text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
              >
                +{chip.label}
              </button>
            ))}
            {amount.raw > 0 && (
              <button
                type="button"
                onClick={() => amount.apply(0)}
                className="rounded-md border border-border px-2.5 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
              >
                Nullstill
              </button>
            )}
          </div>
        </div>
      </div>

      {showMinWarn && (
        <div className="mt-3 flex gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 dark:border-amber-700 dark:bg-amber-950/30">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
          <p className="text-xs text-amber-800 dark:text-amber-200">
            {modelType === "CAPGUARDEKB" ? "Aktiv EKB" : "Aktiv Eiendom"} krever
            min. <span className="font-semibold">{fmt(minThreshold)} kr</span> —
            mangler{" "}
            <span className="font-semibold">
              {fmt(minThreshold - totalPortValue)} kr
            </span>
            .
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Fees section ──────────────────────────────────────────────────────────────

function FeesSection({
  feeOptions,
  modelIsSet,
  priceListOpen,
  onTogglePriceList,
}: {
  feeOptions: { value: string; label: string }[];
  modelIsSet: boolean;
  priceListOpen: boolean;
  onTogglePriceList: () => void;
}) {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<OrderFormData>();
  const feeType = useWatch({ name: "dpFeeType" }) as "onlyFund" | "all";

  return (
    <div>
      <MicroHeader icon={Percent} title="Gebyrer og forvaltning" />
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Tegningsgebyr %" error={errMsg(errors.dpOnOffPercentage)}>
          <input
            type="text"
            {...register("dpOnOffPercentage")}
            placeholder="0"
            className={inputCls}
            onBlur={(e) => {
              const cleaned = e.target.value.replace(/^0+(?=\d)/, "");
              if (cleaned !== e.target.value)
                setValue("dpOnOffPercentage", cleaned);
            }}
          />
        </Field>
        <Field
          label="Forvaltningshonorar"
          hint={
            <button
              type="button"
              onClick={onTogglePriceList}
              className={cn(
                "text-[10px] font-semibold uppercase tracking-wide transition-colors",
                priceListOpen
                  ? "text-primary"
                  : "text-muted-foreground hover:text-primary"
              )}
            >
              Prisliste
            </button>
          }
        >
          <select {...register("dpManagementFee")} className={inputCls}>
            {feeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Fee type">
          <SegmentedControl
            value={feeType}
            disabled={modelIsSet}
            onChange={(value) =>
              setValue("dpFeeType", value, { shouldDirty: true })
            }
            options={[
              { value: "onlyFund", label: "Only fund" },
              { value: "all", label: "All" },
            ]}
          />
        </Field>
      </div>
    </div>
  );
}

// ─── Financing section ─────────────────────────────────────────────────────────

function FinancingSection({
  accountNumbers,
  bankAccount,
  isNew,
  isCapguardPort,
  onCopyAmount,
}: {
  accountNumbers: string[];
  bankAccount: string;
  isNew: boolean;
  isCapguardPort: boolean;
  onCopyAmount: () => void;
}) {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<OrderFormData>();
  const oneOff = useFormattedAmount("dpOnOffAmount");
  const newAccount = useFormattedAccountNumber();
  const newAccountValue = (useWatch({ name: "dpNewAccount" }) ?? "") as string;
  const newAccountCleaned = normalizeAccountNumber(newAccountValue);
  const newAccountHint =
    newAccountCleaned.length === 11 && !validateAccountNumber(newAccountCleaned)
      ? "Kontrollsiffer stemmer ikke – sjekk kontonummeret"
      : undefined;

  return (
    <div>
      <MicroHeader icon={Landmark} title="Finansiering" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Bankkonto" error={errMsg(errors.dpAccount)}>
          <select {...register("dpAccount")} className={inputCls}>
            <option value="">— velg konto —</option>
            <option value="newAccount">Ny konto</option>
            {accountNumbers.length > 0 && <option disabled>──────────</option>}
            {accountNumbers.map((n) => (
              <option key={n} value={n}>
                {formatAccountNumber(n)}
              </option>
            ))}
          </select>
        </Field>
        {!isNew && (
          <Field label="Uttak">
            <input
              type="number"
              min={0}
              step={1000}
              {...register("dpWithdrawal", { valueAsNumber: true })}
              placeholder="0"
              className={inputCls}
            />
          </Field>
        )}
      </div>

      {bankAccount === "newAccount" && (
        <div className="mt-3 max-w-[200px]">
          <Field label="Nytt kontonummer (11 siffer)">
            <div className="relative">
              <input
                type="text"
                {...newAccount.fieldProps}
                placeholder="XXXXXXXXXXX"
                className={cn(inputCls, "pr-8")}
              />
              {newAccountCleaned.length === 11 && (
                <MuiTooltip
                  arrow
                  placement="top"
                  title={newAccountHint ?? VALID_ACCOUNT_TOOLTIP}
                >
                  <span className="absolute right-2 top-1/2 -translate-y-1/2">
                    {newAccountHint ? (
                      <AlertTriangle className="size-4 text-amber-600" />
                    ) : (
                      <Check className="size-4 text-emerald-600" />
                    )}
                  </span>
                </MuiTooltip>
              )}
            </div>
          </Field>
        </div>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Engangsinnskudd" error={errMsg(errors.dpOnOffAmount)}>
          <div className="relative">
            <input
              {...oneOff.fieldProps}
              disabled={!isCapguardPort}
              placeholder="0"
              className={cn(inputCls, "pr-9", !isCapguardPort && "opacity-60")}
            />
            <MuiTooltip
              arrow
              placement="top"
              title="Hent beløpet fra «Beløp å investere»"
            >
              <span className="absolute right-1 top-1/2 flex size-6 -translate-y-1/2">
                <button
                  type="button"
                  aria-label="Hent beløp fra 'Beløp å investere'"
                  disabled={!isCapguardPort}
                  onClick={onCopyAmount}
                  className="flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary disabled:pointer-events-none disabled:opacity-40"
                >
                  <Copy className="size-3.5" />
                </button>
              </span>
            </MuiTooltip>
          </div>
        </Field>
        <Field
          label="Dato for engangsinnskudd"
          error={errMsg(errors.dpOnOffDate)}
        >
          <Controller
            control={control}
            name="dpOnOffDate"
            render={({ field }) => (
              <input
                type="date"
                disabled={!isCapguardPort}
                value={field.value ? norwegianToIso(field.value) : ""}
                onChange={(e) =>
                  field.onChange(
                    e.target.value ? isoToNorwegian(e.target.value) : ""
                  )
                }
                className={cn(inputCls, !isCapguardPort && "opacity-60")}
              />
            )}
          />
        </Field>
      </div>
    </div>
  );
}

// ─── Manual orders section (NOMODEL only) ──────────────────────────────────────

function SecurityAutocomplete({
  value,
  securityId,
  securities,
  onSelect,
  onClear,
}: {
  value: string;
  securityId: number | null;
  securities: QuerySecurity[];
  onSelect: (security: QuerySecurity) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useLayoutEffect(() => {
    if (!open || !inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 260),
      zIndex: 9999,
    });
  }, [open, query]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        !inputRef.current?.contains(e.target as Node) &&
        !listRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery(value);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, value]);

  const normalized = query.trim().toLowerCase();
  const filtered = securities
    .filter(
      (s) =>
        !normalized ||
        s.name?.toLowerCase().includes(normalized) ||
        s.security_code?.toLowerCase().includes(normalized) ||
        s.isin_code?.toLowerCase().includes(normalized)
    )
    .slice(0, 20);

  return (
    <div className="relative min-w-[160px] flex-1">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (securityId != null) onClear();
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Søk fond/instrument"
        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
      />
      {open &&
        createPortal(
          <ul
            ref={listRef}
            style={dropdownStyle}
            className="max-h-52 overflow-auto rounded-md border border-border bg-popover text-xs shadow-xl"
          >
            {filtered.length > 0 ? (
              filtered.map((s) => (
                <li
                  key={s.id}
                  onMouseDown={() => {
                    setQuery(s.name);
                    onSelect(s);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-baseline justify-between gap-2 px-3 py-1.5 hover:bg-primary/10"
                >
                  <span className="truncate font-medium">{s.name}</span>
                  <span className="shrink-0 font-mono text-muted-foreground">
                    {s.security_code}
                  </span>
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-muted-foreground">
                {securities.length === 0
                  ? "Ingen instrumenter tilgjengelig"
                  : `Ingen treff for «${query}»`}
              </li>
            )}
          </ul>,
          document.body
        )}
    </div>
  );
}

// Formats with thousands separators while blurred; shows raw digits + select-all on focus.
function BuyAmountInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [focused, setFocused] = useState(false);
  const numeric = parseFloat(value) || 0;
  const display = focused ? value : numeric ? fmt(numeric) : "";

  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder="Beløp"
      value={display}
      onFocus={(e) => {
        setFocused(true);
        const el = e.target;
        requestAnimationFrame(() => el.select());
      }}
      onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ""))}
      onBlur={() => setFocused(false)}
      className="w-24 rounded-md border border-border bg-background px-2 py-1.5 text-xs tabular-nums focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
    />
  );
}

function ManualOrdersSection({
  selectedPortfolio,
  securities,
}: {
  selectedPortfolio?: Portfolio;
  securities: QuerySecurity[];
}) {
  const { rows: buyRows, update: updateBuy } = useBuyOrders();
  const { rows: sellRows, update: updateSell } = useSellOrders();

  const holdings = useMemo(
    () =>
      (selectedPortfolio?.portfolioReport?.portfolioReportItems ?? []).filter(
        (item) => !item.security.isAccount && item.amount > 0
      ),
    [selectedPortfolio]
  );
  const holdingsKey = holdings.map((h) => h.security.id).join(",");

  const sellRowsRef = useRef(sellRows);
  sellRowsRef.current = sellRows;

  useEffect(() => {
    const fresh = holdings.map(makeSellRowFromHolding);
    updateSell(mergeSellRows(fresh, sellRowsRef.current));
    // Only re-sync when the underlying holdings actually change, not on every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdingsKey]);

  const totalBuy = buyRows.reduce(
    (s, r) => s + (parseFloat(r.amountInput) || 0),
    0
  );

  const addBuyRow = () => updateBuy([...buyRows, makeEmptyBuyRow()]);
  const removeBuyRow = (id: string) =>
    updateBuy(buyRows.filter((r) => r.id !== id));
  const patchBuyRow = (id: string, patch: Partial<BuyOrderRow>) =>
    updateBuy(buyRows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const selectBuySecurity = (id: string, security: QuerySecurity) => {
    const row = buyRows.find((r) => r.id === id);
    if (!row) return;

    const isFund = ["FI", "EF"].includes(security.class2code ?? "");
    patchBuyRow(id, {
      securityId: security.id,
      instrumentCode: security.security_code,
      instrumentName: security.name,
      isinCode: security.isin_code ?? "",
      class1code: security.class1code ?? "",
      class2id: security.class2id ?? "",
      class2code: security.class2code ?? "",
      price: String(security.unitPrice ?? ""),
      type: isFund ? "" : row.type || "Market",
    });
  };

  const clearBuySecurity = (id: string) =>
    patchBuyRow(id, {
      securityId: null,
      instrumentCode: "",
      instrumentName: "",
      isinCode: "",
    });

  const usedHoldingIds = new Set(sellRows.map((r) => r.securityId));
  const addSellRow = () => {
    const next = holdings.find((h) => !usedHoldingIds.has(h.security.id));
    if (!next) return;
    updateSell([...sellRows, makeSellRowFromHolding(next)]);
  };
  const sellAll = () => {
    const rows = mergeSellRows(
      holdings.map(makeSellRowFromHolding),
      sellRows
    ).map((row) => ({ ...row, sellQty: String(row.holding) }));
    updateSell(rows);
  };
  const removeSellRow = (securityId: number) =>
    updateSell(sellRows.filter((r) => r.securityId !== securityId));
  const patchSellRow = (securityId: number, patch: Partial<SellOrderRow>) =>
    updateSell(
      sellRows.map((r) =>
        r.securityId === securityId ? { ...r, ...patch } : r
      )
    );

  return (
    <div>
      <MicroHeader icon={ListOrdered} title="Ordredetaljer (manuell)" />
      <div className="space-y-4">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
              Kjøp{totalBuy > 0 && ` · ${fmt(totalBuy)} kr`}
            </span>
            <button
              type="button"
              onClick={addBuyRow}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-emerald-300 hover:text-emerald-700 dark:hover:text-emerald-400"
            >
              <Plus className="size-3" /> Legg til
            </button>
          </div>
          {buyRows.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[11px] text-muted-foreground">
              Ingen kjøpsordre lagt til.
            </p>
          ) : (
            <div className="space-y-1.5">
              {buyRows.map((row) => (
                <div
                  key={row.id}
                  className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border/70 bg-background/60 p-1.5"
                >
                  <SecurityAutocomplete
                    value={row.instrumentName}
                    securityId={row.securityId}
                    securities={securities}
                    onSelect={(security) => selectBuySecurity(row.id, security)}
                    onClear={() => clearBuySecurity(row.id)}
                  />
                  <BuyAmountInput
                    value={row.amountInput}
                    onChange={(value) =>
                      patchBuyRow(row.id, { amountInput: value })
                    }
                  />
                  <select
                    value={row.type || "Market"}
                    onChange={(e) =>
                      patchBuyRow(row.id, {
                        type: e.target.value as BuyOrderRow["type"],
                      })
                    }
                    className="rounded-md border border-border bg-background py-1.5 pl-1.5 pr-5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                  >
                    <option value="Market">Marked</option>
                    <option value="Limit">Limit</option>
                  </select>
                  {row.type === "Limit" && (
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Limitpris"
                      value={row.limitPrice}
                      onChange={(e) =>
                        patchBuyRow(row.id, { limitPrice: e.target.value })
                      }
                      className="w-20 rounded-md border border-border bg-background px-2 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => removeBuyRow(row.id)}
                    aria-label="Fjern rad"
                    className="ml-auto flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-rose-700 dark:text-rose-400">
              Salg
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={sellAll}
                disabled={holdings.length === 0}
                className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-[10px] font-semibold text-rose-700 transition-colors hover:border-rose-300 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-rose-900 dark:text-rose-400 dark:hover:bg-rose-950/30"
              >
                Selg alt
              </button>
              <button
                type="button"
                onClick={addSellRow}
                disabled={
                  holdings.length === 0 || sellRows.length >= holdings.length
                }
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-rose-300 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:text-rose-400"
              >
                <Plus className="size-3" /> Legg til
              </button>
            </div>
          </div>
          {holdings.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[11px] text-muted-foreground">
              Ingen beholdning å selge fra.
            </p>
          ) : sellRows.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[11px] text-muted-foreground">
              Ingen salgsordre lagt til.
            </p>
          ) : (
            <div className="space-y-1.5">
              <div className="grid grid-cols-[minmax(160px,1fr)_6rem_5rem_2.75rem_6rem_1.75rem] items-center gap-1.5 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Instrument</span>
                <span className="text-right">Beholdning</span>
                <span className="col-span-2">Antall</span>
                <span>Type</span>
              </div>
              {sellRows.map((row) => (
                <div
                  key={row.securityId}
                  className="grid grid-cols-[minmax(160px,1fr)_6rem_5rem_2.75rem_6rem_1.75rem] items-center gap-1.5 rounded-lg border border-border/70 bg-background/60 p-1.5"
                >
                  <select
                    value={String(row.securityId)}
                    onChange={(e) => {
                      const holding = holdings.find(
                        (h) => h.security.id === Number(e.target.value)
                      );
                      if (holding)
                        patchSellRow(
                          row.securityId,
                          makeSellRowFromHolding(holding)
                        );
                    }}
                    className="min-w-0 rounded-md border border-border bg-background px-2 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                  >
                    {holdings
                      .filter(
                        (h) =>
                          h.security.id === row.securityId ||
                          !usedHoldingIds.has(h.security.id)
                      )
                      .map((h) => (
                        <option key={h.security.id} value={h.security.id}>
                          {h.security.name}
                        </option>
                      ))}
                  </select>
                  <div className="rounded-md border border-border/70 bg-muted/40 px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-foreground">
                    {formatNumber(row.holding, 2)}
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Antall"
                    value={row.sellQty}
                    onChange={(e) =>
                      patchSellRow(row.securityId, {
                        sellQty: e.target.value.replace(/[^\d.,]/g, ""),
                      })
                    }
                    className="min-w-0 rounded-md border border-border bg-background px-2 py-1.5 text-xs tabular-nums focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      patchSellRow(row.securityId, {
                        sellQty: String(row.holding),
                      })
                    }
                    className="rounded-md border border-border px-1.5 py-1.5 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    Alt
                  </button>
                  <div className="flex items-center gap-1.5">
                    <select
                      value={row.type || "Market"}
                      onChange={(e) =>
                        patchSellRow(row.securityId, {
                          type: e.target.value as SellOrderRow["type"],
                        })
                      }
                      className="w-full rounded-md border border-border bg-background py-1.5 pl-1.5 pr-5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                    >
                      <option value="Market">Marked</option>
                      <option value="Limit">Limit</option>
                    </select>
                    {row.type === "Limit" && (
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="Limitpris"
                        value={row.limitPrice}
                        onChange={(e) =>
                          patchSellRow(row.securityId, {
                            limitPrice: e.target.value,
                          })
                        }
                        className="w-20 shrink-0 rounded-md border border-border bg-background px-2 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSellRow(row.securityId)}
                    aria-label="Fjern rad"
                    className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Agreements section (savings + withdrawal) ─────────────────────────────────

function AgreementsSection({
  securities,
  accountNumbers,
}: {
  securities: QuerySecurity[];
  accountNumbers: string[];
}) {
  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<OrderFormData>();
  const modelType = useWatch({ name: "dpModelType" }) as string | undefined;
  const savingsEnabled = useWatch({ name: "dpSavingsEnabled" }) as boolean;
  const withdrawalEnabled = useWatch({
    name: "dpWithdrawalEnabled",
  }) as boolean;
  const savingsAvtalegiro = useWatch({
    name: "dpSavingsAvtalegiro",
  }) as boolean;
  const hasModel = !!modelType && modelType !== "";
  const isNoModel = !modelType || modelType === "NOMODEL";

  const {
    fields: fundFields,
    append: appendFund,
    remove: removeFund,
    update: updateFund,
  } = useFieldArray<SavingsFundsFieldValues, "dpSavingsFunds">({
    control: control as unknown as Control<SavingsFundsFieldValues>,
    name: "dpSavingsFunds",
  });

  const fundSumPct = fundFields.reduce((sum, f) => sum + (f.pct || 0), 0);

  useEffect(() => {
    // A model portfolio invests savings automatically — manual fund picks don't apply
    if (!isNoModel) setValue("dpSavingsFunds", []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNoModel]);

  const toggleSavings = (next: boolean) => {
    setValue("dpSavingsEnabled", next, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (!next) {
      setValue("dpSavingsAmount", undefined);
      setValue("dpSavingsDayOfMonth", undefined);
      setValue("dpSavingsAvtalegiro", false);
      setValue("dpSavingsFunds", []);
    }
  };
  const toggleWithdrawal = (next: boolean) => {
    setValue("dpWithdrawalEnabled", next, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (!next) {
      setValue("dpWdAccount", "");
      setValue("dpWdDayOfMonth", undefined);
      setValue("dpWdAmount", undefined);
    }
  };

  return (
    <div>
      <MicroHeader icon={FileCheck2} title="Avtaler" />
      <div className="grid gap-3 sm:grid-cols-2">
        <ToggleCard
          icon={PiggyBank}
          label="Spareavtale"
          hint="Faste, periodiske innskudd i modellen"
          checked={savingsEnabled}
          disabled={!hasModel}
          onChange={toggleSavings}
        />
        <ToggleCard
          icon={Landmark}
          label="Uttaksavtale"
          hint="Faste, periodiske uttak fra kontoen"
          checked={withdrawalEnabled}
          disabled={!hasModel}
          onChange={toggleWithdrawal}
        />
      </div>

      {savingsEnabled && (
        <div className="mt-3 space-y-3 rounded-lg border border-emerald-200/60 bg-emerald-50/40 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/10">
          <div className="grid gap-3 sm:grid-cols-3">
            <AmountField
              name="dpSavingsAmount"
              label="Beløp per periode"
              error={errMsg(errors.dpSavingsAmount)}
            />
            <Field label="Trekkdato" error={errMsg(errors.dpSavingsDayOfMonth)}>
              <select
                {...register("dpSavingsDayOfMonth", { valueAsNumber: true })}
                className={inputCls}
              >
                <option value="">Velg dag</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <option key={day} value={day}>
                    {day}.
                  </option>
                ))}
              </select>
            </Field>
            <div className="flex items-end pb-1.5">
              <label className="flex items-center gap-2 text-xs text-foreground">
                <Switch
                  size="sm"
                  checked={savingsAvtalegiro}
                  onCheckedChange={(checked) =>
                    setValue("dpSavingsAvtalegiro", checked, {
                      shouldDirty: true,
                    })
                  }
                />
                AvtaleGiro
              </label>
            </div>
          </div>

          {isNoModel && (
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[11px] font-semibold text-foreground/80">
                  Fondsfordeling
                </span>
                <span
                  className={cn(
                    "text-[10px] font-semibold",
                    fundSumPct > 100
                      ? "text-destructive"
                      : "text-muted-foreground"
                  )}
                >
                  Sum {fundSumPct}%
                </span>
              </div>
              <div className="space-y-1.5">
                {fundFields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-1.5">
                    <input
                      type="text"
                      list="of-funds"
                      placeholder="Fond"
                      value={field.name}
                      onChange={(e) =>
                        updateFund(index, {
                          name: e.target.value,
                          pct: field.pct,
                        })
                      }
                      className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                    />
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={field.pct}
                      onChange={(e) =>
                        updateFund(index, {
                          name: field.name,
                          pct: Number(e.target.value) || 0,
                        })
                      }
                      className="w-16 rounded-md border border-border bg-background px-2 py-1.5 text-xs tabular-nums focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                    />
                    <span className="text-[10px] text-muted-foreground">%</span>
                    <button
                      type="button"
                      onClick={() => removeFund(index)}
                      aria-label="Fjern fond"
                      className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                ))}
                {fundFields.length < MAX_SAVINGS_FUNDS && (
                  <button
                    type="button"
                    onClick={() => appendFund({ name: "", pct: 0 })}
                    className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-emerald-300 hover:text-emerald-700 dark:hover:text-emerald-400"
                  >
                    <Plus className="size-3" /> Legg til fond
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {withdrawalEnabled && (
        <div className="mt-3 grid gap-3 rounded-lg border border-blue-200/60 bg-blue-50/40 p-3 dark:border-blue-900/50 dark:bg-blue-950/10 sm:grid-cols-3">
          <Field label="Konto" error={errMsg(errors.dpWdAccount)}>
            <select {...register("dpWdAccount")} className={inputCls}>
              <option value="">— velg konto —</option>
              {accountNumbers.map((n) => (
                <option key={n} value={n}>
                  {formatAccountNumber(n)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Uttaksdato" error={errMsg(errors.dpWdDayOfMonth)}>
            <select
              {...register("dpWdDayOfMonth", { valueAsNumber: true })}
              className={inputCls}
            >
              <option value="">Velg dag</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                <option key={day} value={day}>
                  {day}.
                </option>
              ))}
            </select>
          </Field>
          <AmountField
            name="dpWdAmount"
            label="Beløp per periode"
            error={errMsg(errors.dpWdAmount)}
          />
        </div>
      )}
      <datalist id="of-funds">
        {securities
          .filter((s) => ["FI", "EF"].includes(s.class2code))
          .slice(0, 300)
          .map((s) => (
            <option key={s.id} value={s.name} />
          ))}
      </datalist>
    </div>
  );
}

// ─── Transfer authorization section (ASK / IPS only) ───────────────────────────

function TransferSection({ accountType }: { accountType: "ASK" | "IPS" }) {
  const { register, setValue } = useFormContext<OrderFormData>();
  const moveAll = useWatch({ name: "moveAll" }) as boolean;
  const askToAsk = useWatch({ name: "askToAsk" }) as boolean;
  const instrumentValues = useWatch({
    name: Array.from(
      { length: MAX_INSTRUMENT_ROWS },
      (_, index) => `Instrument${index + 1}` as Path<OrderFormData>
    ),
  }) as Array<string | undefined>;

  const activeRowCount = instrumentValues.reduce(
    (last, value, index) => (value ? index + 1 : last),
    0
  );
  const [rowCount, setRowCount] = useState(activeRowCount);

  useEffect(() => {
    if (activeRowCount > rowCount) setRowCount(activeRowCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRowCount]);

  useEffect(() => {
    if (accountType === "IPS")
      setValue("askToAsk", false, { shouldDirty: false });
  }, [accountType, setValue]);

  const addRow = () =>
    setRowCount((count) => Math.min(MAX_INSTRUMENT_ROWS, count + 1));
  const removeRow = (row: number) => {
    setValue(fieldPath(`Instrument${row}`), "", { shouldDirty: true });
    setValue(fieldPath(`InstrumentAmt${row}`), undefined, {
      shouldDirty: true,
    });
    setRowCount((count) => Math.max(0, count - 1));
  };

  return (
    <div>
      <MicroHeader icon={ArrowRightLeft} title="Flyttefullmakt" />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Avleverende tilbyder">
          <input
            type="text"
            {...register("moveFrom")}
            placeholder="F.eks. Nordnet"
            className={inputCls}
          />
        </Field>
        <Field label="KontoID / kontonummer">
          <input
            type="text"
            {...register("moveFromId")}
            placeholder="KontoID eller kontonummer"
            className={inputCls}
          />
        </Field>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <ToggleCard
          icon={ArrowRightLeft}
          label="Flytt hele beholdningen"
          hint="Ingen enkeltinstrumenter nødvendig"
          checked={moveAll}
          onChange={(next) => setValue("moveAll", next, { shouldDirty: true })}
        />
        {accountType === "ASK" && (
          <ToggleCard
            icon={Check}
            label="ASK til ASK"
            hint="Overføring mellom to aksjesparekontoer"
            checked={askToAsk}
            onChange={(next) =>
              setValue("askToAsk", next, { shouldDirty: true })
            }
          />
        )}
      </div>

      {!moveAll && (
        <div className="mt-3 space-y-1.5">
          {Array.from({ length: rowCount }, (_, index) => {
            const row = index + 1;
            return (
              <div key={row} className="flex items-center gap-1.5">
                <input
                  type="text"
                  list="of-securities"
                  {...register(fieldPath(`Instrument${row}`))}
                  placeholder="Navn, ISIN eller ekstern ID"
                  className="min-w-0 flex-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                />
                <input
                  type="text"
                  inputMode="numeric"
                  {...register(fieldPath(`InstrumentAmt${row}`), {
                    valueAsNumber: true,
                  })}
                  placeholder="Beløp"
                  className="w-28 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs tabular-nums focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                />
                <button
                  type="button"
                  onClick={() => removeRow(row)}
                  aria-label="Fjern instrument"
                  className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            );
          })}
          {rowCount < MAX_INSTRUMENT_ROWS && (
            <button
              type="button"
              onClick={addRow}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2.5 py-1.5 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <Plus className="size-3.5" /> Legg til instrument
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sidebar: summary, projection & price list ─────────────────────────────────

function SummaryCard({
  portfolios,
  modelOptions,
}: {
  portfolios: Portfolio[];
  modelOptions: { value: string; label: string }[];
}) {
  const portfolioId = useWatch({ name: "dpPortfolioId" }) as string;
  const portType = useWatch({ name: "dpPortType" }) as string;
  const modelType = useWatch({ name: "dpModelType" }) as string;
  const amountToInvest =
    (useWatch({ name: "dpAmountToInvest" }) as number) || 0;
  const subscriptionFeePct = useWatch({ name: "dpOnOffPercentage" }) as string;
  const managementFee = useWatch({ name: "dpManagementFee" }) as string;
  const bankAccount = useWatch({ name: "dpAccount" }) as string;
  const newAccount = useWatch({ name: "dpNewAccount" }) as string;
  const newAccountIsValid =
    bankAccount === "newAccount" && validateAccountNumber(newAccount || "");

  const selected = portfolios.find((p) => String(p.id) === String(portfolioId));

  const rows: { label: string; value: string; verified?: boolean }[] = [
    {
      label: "Portefølje",
      value: selected?.name || (portfolioId === "NEW" ? "Ny portefølje" : "—"),
    },
    {
      label: "Type",
      value:
        portfolioTypeOptions.find((o) => o.value === portType)?.label || "—",
    },
    {
      label: "Modell",
      value: modelOptions.find((o) => o.value === modelType)?.label || "—",
    },
    {
      label: "Beløp",
      value: amountToInvest ? `${fmt(amountToInvest)} kr` : "—",
    },
    { label: "Tegningsgebyr", value: `${subscriptionFeePct || "0"} %` },
    {
      label: "Forvaltning",
      value:
        managementFee === "1.5" ? "Listepris" : `${managementFee || "—"} %`,
    },
    {
      label: "Bankkonto",
      value:
        bankAccount === "newAccount"
          ? formatAccountNumber(newAccount) || "—"
          : formatAccountNumber(bankAccount) || "—",
      verified: newAccountIsValid,
    },
  ];

  return (
    <div className="rounded-xl border border-border/70 bg-card p-3">
      <div className="mb-2 flex items-center gap-2 border-b border-border/50 pb-2">
        <Wallet className="size-3.5 text-primary" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Sammendrag
        </span>
      </div>
      <div className="space-y-1.5">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-3 text-[11px]"
          >
            <span className="text-muted-foreground">{row.label}</span>
            <span className="flex max-w-[60%] items-center justify-end gap-1 truncate text-right font-medium tabular-nums text-foreground">
              {row.value}
              {row.verified && (
                <MuiTooltip arrow placement="top" title={VALID_ACCOUNT_TOOLTIP}>
                  <span className="inline-flex shrink-0">
                    <Check
                      aria-label="Verifisert kontonummer"
                      className="size-3 text-emerald-600 dark:text-emerald-400"
                    />
                  </span>
                </MuiTooltip>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

type AfterHolding = {
  key: string;
  name: string;
  marketValue: number;
  isEquity: boolean;
};

function useProjection(
  portfolios: Portfolio[],
  modelsData: QueryModelPosition[]
) {
  const portfolioId = useWatch({ name: "dpPortfolioId" }) as string;
  const modelType = useWatch({ name: "dpModelType" }) as string;
  const modelTypeName = useWatch({ name: "dpModelTypeName" }) as string;
  const rawBuyOrders = useWatch({ name: "buyOrders" }) as BuyOrderRow[] | null;
  const rawSellOrders = useWatch({ name: "sellOrders" }) as
    | SellOrderRow[]
    | null;
  const amountToInvest =
    (useWatch({ name: "dpAmountToInvest" }) as number) ?? 0;
  const withdrawal = (useWatch({ name: "dpWithdrawal" }) as number) ?? 0;
  const sumInsValue = (useWatch({ name: "dpSumInsValue" }) as number) ?? 0;
  const sumAvCash = (useWatch({ name: "dpSumAvCash" }) as number) ?? 0;

  const buyOrders = useMemo(() => rawBuyOrders ?? [], [rawBuyOrders]);
  const sellOrders = useMemo(() => rawSellOrders ?? [], [rawSellOrders]);

  const isNew = portfolioId === "NEW";
  const isNoModel = !modelType || modelType === "NOMODEL";
  const selected = portfolios.find((p) => String(p.id) === String(portfolioId));

  const currentItems = useMemo(
    () => selected?.portfolioReport?.portfolioReportItems ?? [],
    [selected]
  );
  const currentCash = selected?.portfolioReport?.accountBalance ?? 0;
  const currentMv = selected?.portfolioReport?.marketValue ?? 0;
  const currentEquityMv = currentItems
    .filter((i) => isEquityByClass1(i.security?.classType1?.code ?? ""))
    .reduce((s, i) => s + (i.marketValue ?? 0), 0);
  const currentEquityPct =
    currentMv > 0 ? (currentEquityMv / currentMv) * 100 : 0;

  const modelEquityFraction = useMemo(() => {
    if (isNoModel) return null;
    const positions = modelsData.filter((p) => p.short_name === modelType);
    if (positions.length > 0) {
      const total = positions.reduce((s, p) => s + p.share_of_portfolio, 0);
      const stockTotal = positions
        .filter((p) => isEquityByModelClass1(p.class_type1))
        .reduce((s, p) => s + p.share_of_portfolio, 0);
      return total > 0 ? stockTotal / total : 0;
    }
    return getModelEquityFractionFallback(modelType);
  }, [modelType, isNoModel, modelsData]);

  const modelDisplayName = useMemo(() => {
    if (!modelType || isNoModel) return null;
    return MODEL_DISPLAY_NAMES[modelType] ?? modelTypeName ?? modelType;
  }, [modelType, modelTypeName, isNoModel]);

  const afterTotalModel = isNew
    ? amountToInvest
    : Math.max(0, sumInsValue + sumAvCash + amountToInvest - withdrawal);
  const afterEquityPctModel =
    modelEquityFraction != null ? modelEquityFraction * 100 : 0;

  const { afterHoldings, afterCash } = useMemo(() => {
    if (!isNoModel)
      return { afterHoldings: [] as AfterHolding[], afterCash: 0 };
    const map = new Map<string, AfterHolding>();
    for (const item of currentItems) {
      const key = String(item.security.id);
      map.set(key, {
        key,
        name: item.security.name,
        marketValue: item.marketValue ?? 0,
        isEquity: isEquityByClass1(item.security?.classType1?.code ?? ""),
      });
    }
    for (const sell of sellOrders) {
      const qty = parseFloat(sell.sellQty) || 0;
      if (qty <= 0) continue;
      const key = String(sell.securityId);
      const existing = map.get(key);
      if (!existing) continue;
      const remaining = existing.marketValue - qty * sell.marketUnitPrice;
      if (remaining <= 1e-6) map.delete(key);
      else map.set(key, { ...existing, marketValue: remaining });
    }
    for (const buy of buyOrders) {
      if (!buy.instrumentName) continue;
      const amt = parseFloat(buy.amountInput) || 0;
      if (amt <= 0) continue;
      const key = buy.securityId
        ? String(buy.securityId)
        : `buy_${buy.instrumentName}`;
      const existing = map.get(key);
      if (existing)
        map.set(key, { ...existing, marketValue: existing.marketValue + amt });
      else
        map.set(key, {
          key,
          name: buy.instrumentName,
          marketValue: amt,
          isEquity: isEquityByClass1(buy.class1code),
        });
    }
    const totalBuyAmt = buyOrders.reduce(
      (s, r) => s + (parseFloat(r.amountInput) || 0),
      0
    );
    const totalSellAmt = sellOrders.reduce(
      (s, r) => s + (parseFloat(r.sellQty) || 0) * (r.marketUnitPrice ?? 0),
      0
    );
    const cash =
      currentCash + amountToInvest + totalSellAmt - totalBuyAmt - withdrawal;
    return {
      afterHoldings: Array.from(map.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
      afterCash: cash,
    };
  }, [
    isNoModel,
    currentItems,
    currentCash,
    amountToInvest,
    withdrawal,
    buyOrders,
    sellOrders,
  ]);

  const afterSecMv = afterHoldings.reduce((s, h) => s + h.marketValue, 0);
  const afterEquityMv = afterHoldings
    .filter((h) => h.isEquity)
    .reduce((s, h) => s + h.marketValue, 0);
  const afterTotalNoModel = afterSecMv + Math.max(afterCash, 0);
  const afterEquityPctNoModel =
    afterTotalNoModel > 0 ? (afterEquityMv / afterTotalNoModel) * 100 : 0;

  const hasChanges =
    buyOrders.some((r) => r.instrumentName && parseFloat(r.amountInput) > 0) ||
    sellOrders.some((r) => parseFloat(r.sellQty) > 0) ||
    amountToInvest > 0 ||
    withdrawal > 0;

  return {
    selected,
    isNew,
    isNoModel,
    currentMv,
    currentEquityPct,
    afterTotal: isNoModel ? afterTotalNoModel : afterTotalModel,
    afterEquityPct: isNoModel ? afterEquityPctNoModel : afterEquityPctModel,
    afterEquityPctModel,
    modelDisplayName,
    modelEquityFraction,
    afterHoldings,
    afterCash,
    afterTotalNoModel,
    amountToInvest,
    hasChanges,
  };
}

function ProjectionCard({
  portfolios,
  modelsData,
}: {
  portfolios: Portfolio[];
  modelsData: QueryModelPosition[];
}) {
  const p = useProjection(portfolios, modelsData);

  if (!p.selected && !p.isNew) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-border/70 bg-card p-6 text-center text-muted-foreground">
        <TrendingUp className="size-6 opacity-30" />
        <p className="text-[11px]">Velg en portefølje for å se projeksjon</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/70 bg-card p-3">
      <div className="mb-2 flex items-center gap-2 border-b border-border/50 pb-2">
        <TrendingUp className="size-3.5 text-primary" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Porteføljeprojeksjon
        </span>
      </div>

      {p.selected && (
        <div className="grid grid-cols-2 gap-2">
          <StatChip label="Før" value={fmt(p.currentMv)} />
          <StatChip
            label="Etter"
            value={fmt(p.afterTotal)}
            highlight={p.hasChanges}
          />
        </div>
      )}
      <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] text-muted-foreground">
        <span>Aksjer {formatNumber(p.currentEquityPct, 1)}%</span>
        <span className={cn(p.hasChanges && "font-semibold text-primary")}>
          Aksjer {formatNumber(p.afterEquityPct, 1)}%
        </span>
      </div>

      {!p.isNoModel && p.modelEquityFraction != null && (
        <div className="mt-3 overflow-hidden rounded-lg border border-border/60">
          <div className="flex items-center justify-between border-b border-border/40 px-2.5 py-1.5">
            <span className="text-[11px] font-semibold text-foreground">
              {p.modelDisplayName}
            </span>
            {p.amountToInvest > 0 && (
              <span className="text-[10px] tabular-nums text-muted-foreground">
                + {fmt(p.amountToInvest)} kr
              </span>
            )}
          </div>
          <div className="px-2.5 pt-2.5">
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="bg-primary"
                style={{ width: `${p.afterEquityPctModel}%` }}
              />
              <div
                className="bg-emerald-400"
                style={{ width: `${100 - p.afterEquityPctModel}%` }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 px-2.5 py-2">
            <span className="text-[10px] text-muted-foreground">
              <span className="mr-1 inline-block size-1.5 rounded-full bg-primary" />
              Aksjer {formatNumber(p.afterEquityPctModel, 1)}%
            </span>
            <span className="text-[10px] text-muted-foreground">
              <span className="mr-1 inline-block size-1.5 rounded-full bg-emerald-400" />
              Renter {formatNumber(100 - p.afterEquityPctModel, 1)}%
            </span>
          </div>
        </div>
      )}

      {p.isNoModel && (p.afterHoldings.length > 0 || p.afterCash !== 0) && (
        <div className="mt-3 overflow-hidden rounded-lg border border-border/60">
          <div className="flex items-center justify-between border-b border-border/40 bg-muted/30 px-2.5 py-1.5">
            <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">
              Beholdning etter
            </span>
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {fmt(p.afterTotalNoModel)}
            </span>
          </div>
          <div className="max-h-56 divide-y divide-border/40 overflow-y-auto">
            {p.afterHoldings.map((h) => {
              const pct =
                p.afterTotalNoModel > 0
                  ? (h.marketValue / p.afterTotalNoModel) * 100
                  : 0;
              return (
                <div
                  key={h.key}
                  className="flex items-center justify-between gap-2 px-2.5 py-1.5"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span
                      className={cn(
                        "size-1.5 shrink-0 rounded-full",
                        h.isEquity ? "bg-primary" : "bg-emerald-400"
                      )}
                    />
                    <p className="truncate text-[11px]">{h.name}</p>
                  </div>
                  <div className="shrink-0 text-right text-[11px] tabular-nums">
                    {fmt(h.marketValue)}{" "}
                    <span className="text-[9px] text-muted-foreground">
                      {formatNumber(pct, 1)}%
                    </span>
                  </div>
                </div>
              );
            })}
            {p.afterCash !== 0 && (
              <div className="flex items-center justify-between px-2.5 py-1.5 text-[11px] text-muted-foreground">
                <span>Cash</span>
                <span
                  className={cn(
                    "tabular-nums",
                    p.afterCash < 0 && "text-destructive"
                  )}
                >
                  {fmt(p.afterCash)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PriceListCard({
  open,
  onToggle,
  amountToInvest,
  modelType,
  managementFee,
}: {
  open: boolean;
  onToggle: () => void;
  amountToInvest: number;
  modelType: string;
  managementFee: string;
}) {
  const mnok = amountToInvest / 1_000_000;
  const activeTier =
    mnok >= 10
      ? "10+"
      : mnok >= 5
      ? "5-10"
      : mnok >= 2
      ? "2-5"
      : amountToInvest > 0
      ? "<2"
      : null;

  const modelToKey: Record<
    string,
    "active" | "ekb" | "realEstate" | "bonds" | "safe"
  > = {
    CAPGUARD75: "active",
    CAPGUARD60: "active",
    CAPGUARD45: "active",
    CAPGUARD25: "active",
    CAPGUARDAKSJER: "active",
    AKSJER: "active",
    CAPGUARDEKB: "ekb",
    CAPGUARDEIENDOM: "realEstate",
    CAPGUARDRENTER: "bonds",
    FONDSOBLIGASJON: "bonds",
    CAPGUARDTRYGG: "safe",
  };
  const activeProductKey = modelType ? modelToKey[modelType] ?? null : null;
  const products = [
    { label: "Aktiv", key: "active" as const },
    { label: "EKB", key: "ekb" as const },
    { label: "Eiendom", key: "realEstate" as const },
    { label: "Renter", key: "bonds" as const },
    { label: "Trygg", key: "safe" as const },
  ];
  const feeRows = PRICELIST_ROWS.filter(
    (r) =>
      r.tier !== "Kurtasje belastes" &&
      r.tier !== "Minstetegning" &&
      r.tier !== "Depot" &&
      r.tier !== "Inn/Ut"
  );

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border",
        open ? "border-primary/40" : "border-border/70"
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex w-full items-center justify-between px-3 py-2 transition-colors",
          open ? "bg-primary/10" : "hover:bg-muted/40"
        )}
      >
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          <BadgePercent className="size-3 text-primary" /> Prisliste
        </span>
        <ChevronDown
          className={cn(
            "size-3 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-border/40">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="bg-muted/30">
                <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground" />
                {feeRows.map((row) => (
                  <th
                    key={row.tier}
                    className="px-2 py-1.5 text-right font-semibold tabular-nums text-muted-foreground"
                  >
                    {row.tier}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {products.map(({ label, key }) => (
                <tr key={label}>
                  <td className="whitespace-nowrap px-2 py-1.5 font-semibold text-foreground">
                    {label}
                  </td>
                  {feeRows.map((row) => {
                    const isActive =
                      managementFee === "1.5" &&
                      activeProductKey === key &&
                      row.tier === activeTier;
                    return (
                      <td
                        key={row.tier}
                        className={cn(
                          "px-2 py-1.5 text-right tabular-nums font-medium",
                          isActive
                            ? "rounded bg-primary text-primary-foreground"
                            : "text-foreground"
                        )}
                      >
                        {row[key]}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── buildExistData (matches the contract consumed by onDocumentCreated) ──────

function buildExistData(portfolio: Portfolio | undefined) {
  if (!portfolio) return { isExisting: false };
  const managementFee = portfolio.fees?.find((f) => f.type === "Management");
  const karOkAccount = portfolio.accounts?.find((a) =>
    a.tagsAsSet.includes("KAR-OK")
  );
  return {
    dpPortfolioId: String(portfolio.id),
    dpPortfolioName: portfolio.name,
    dpPortfolioShortName: portfolio.shortName ?? "",
    dpPortType: portfolio.typeCode ?? "",
    dpModelType:
      portfolio.modelPortfolio?.linkedPortfolio?.shortName ??
      portfolio.modelPortfolio?.code ??
      "",
    dpModelTypeName:
      portfolio.modelPortfolio?.linkedPortfolio?.name ??
      portfolio.modelPortfolio?.name ??
      "",
    dpAmountToInvest: 0,
    dpSumAvCash: portfolio.portfolioReport?.accountBalance ?? 0,
    dpSumInsValue: portfolio.portfolioReport?.positionMarketValue ?? 0,
    dpOnOffPercentage: "0",
    dpManagementFee:
      managementFee?.feePercent !== undefined
        ? String(managementFee.feePercent)
        : "1.5",
    dpFeeType: "all",
    dpOnOffAmount: 0,
    dpOnOffDate: "",
    dpAccount: karOkAccount?.number ?? "",
    dpNewAccount: "",
    dpWithdrawal: 0,
    ordreModul: "no",
    buyOrders: [],
    sellOrders: [],
    isExisting: true,
  };
}

// ─── Content (needs FormProvider context) ──────────────────────────────────────

function OrderFormCleanContent({
  portfolios,
  securities,
  isCompany,
  modelsData,
}: {
  portfolios: Portfolio[];
  securities: QuerySecurity[];
  isCompany: boolean;
  modelsData: QueryModelPosition[];
}) {
  const { setValue, clearErrors } = useFormContext<OrderFormData>();

  const portfolioId = useWatch({ name: "dpPortfolioId" }) as string;
  const portType = useWatch({ name: "dpPortType" }) as string;
  const modelType = useWatch({ name: "dpModelType" }) as string;
  const bankAccount = useWatch({ name: "dpAccount" }) as string;
  const amountToInvest =
    (useWatch({ name: "dpAmountToInvest" }) as number) || 0;
  const sumAvCash = (useWatch({ name: "dpSumAvCash" }) as number) || 0;
  const sumInsValue = (useWatch({ name: "dpSumInsValue" }) as number) || 0;
  const managementFee = useWatch({ name: "dpManagementFee" }) as string;

  const isNew = portfolioId === "NEW";
  const isNoModel = modelType === "NOMODEL";
  const modelIsSet = !!modelType && !isNoModel;
  const isCapguardPort = CAPGUARD_PORT_TYPES.includes(portType ?? "");
  const isAsk = portType === "22";
  const isIps = portType === "23";
  const isTransferAccount = isAsk || isIps;
  const transferAccountType = isIps ? "IPS" : "ASK";
  const totalPortValue = sumAvCash + sumInsValue + amountToInvest;
  const minThreshold = getMinimumMandateAmount(modelType);
  const showMinWarn =
    minThreshold > 0 && totalPortValue > 0 && totalPortValue < minThreshold;
  const selected = portfolios.find((p) => String(p.id) === String(portfolioId));
  const prevPortfolioIdRef = useRef(portfolioId);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const oneOff = useFormattedAmount("dpOnOffAmount");
  const copyAmountToOneOff = () => oneOff.apply(Math.max(0, amountToInvest));
  const scrollToTransferSection = () => {
    const container = scrollContainerRef.current;
    const target = document.getElementById("of-transfer-section");
    if (!container || !target) return;
    // Scroll only our own container — scrollIntoView can also scroll the
    // surrounding dialog, leaving a large empty gap below the content.
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const top = container.scrollTop + (targetRect.top - containerRect.top) - 12;
    container.scrollTo({ top, behavior: "smooth" });
  };

  useEffect(() => {
    if (portfolioId === prevPortfolioIdRef.current) return;
    prevPortfolioIdRef.current = portfolioId;
    if (selected) {
      setValue("dpPortType", selected.typeCode ?? "", { shouldValidate: true });
      setValue(
        "dpSumInsValue",
        selected.portfolioReport?.positionMarketValue ?? 0
      );
      setValue("dpPortfolioName", selected.name);
      setValue("dpSumAvCash", selected.portfolioReport?.accountBalance ?? 0);
      const linked = selected.modelPortfolio?.linkedPortfolio?.shortName;
      if (linked) {
        setValue("dpModelType", linked, { shouldValidate: true });
        setValue(
          "dpModelTypeName",
          selected.modelPortfolio?.linkedPortfolio?.name ??
            selected.modelPortfolio?.name ??
            ""
        );
      } else {
        setValue("dpModelType", "");
        setValue("dpModelTypeName", "");
      }
      const mgmtFee = selected.fees?.find((f) => f.type === "Management");
      setValue(
        "dpManagementFee",
        mgmtFee?.feePercent !== undefined ? String(mgmtFee.feePercent) : "1.5"
      );
      const karOk = selected.accounts?.find((a) =>
        a.tagsAsSet.includes("KAR-OK")
      );
      setValue("dpAccount", karOk?.number ?? "");
    } else if (isNew) {
      setValue("dpPortType", "");
      setValue("dpPortfolioName", "Ny portefølje");
      setValue("dpSumInsValue", 0);
      setValue("dpSumAvCash", 0);
      setValue("dpModelType", "");
      setValue("dpModelTypeName", "");
      setValue("dpManagementFee", "1.5");
      setValue("dpAccount", "");
      setValue("dpWithdrawal", 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  const existingModelValue =
    selected?.modelPortfolio?.linkedPortfolio?.shortName ?? "";
  const modelOptions = useMemo(() => {
    const base = getModelOptions(portType ?? "");
    return existingModelValue &&
      !base.find((o) => o.value === existingModelValue)
      ? [{ value: existingModelValue, label: existingModelValue }, ...base]
      : base;
  }, [portType, existingModelValue]);

  useEffect(() => {
    if (
      modelType &&
      modelType !== existingModelValue &&
      !modelOptions.find((o) => o.value === modelType)
    ) {
      setValue("dpModelType", "");
      setValue("dpModelTypeName", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portType]);

  useEffect(() => {
    if (modelIsSet) setValue("dpFeeType", "all");
  }, [modelIsSet, setValue]);
  useEffect(() => {
    if (!modelIsSet && managementFee === "1.5") {
      setValue("dpManagementFee", "0", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [managementFee, modelIsSet, setValue]);
  useEffect(() => {
    setValue("ordreModul", isNoModel ? "yes" : "no");
  }, [isNoModel, setValue]);
  useEffect(() => {
    if (!isNoModel) {
      setValue("buyOrders", []);
      setValue("sellOrders", []);
    }
  }, [isNoModel, setValue]);
  useEffect(() => {
    if (isTransferAccount) return;
    setValue("askToAsk", false);
    setValue("moveAll", false);
    setValue("moveFrom", "");
    setValue("moveFromId", "");
    for (let index = 1; index <= MAX_INSTRUMENT_ROWS; index += 1) {
      setValue(fieldPath(`Instrument${index}`), "");
      setValue(fieldPath(`InstrumentAmt${index}`), undefined);
    }
  }, [isTransferAccount, setValue]);
  useEffect(() => {
    if (!showMinWarn) clearErrors("dpAmountToInvest");
  }, [clearErrors, showMinWarn]);

  const feeOptions = getManagementFeeOptions(
    selected?.fees?.find((f) => f.type === "Management")?.feePercent
  ).filter((option) => modelIsSet || option.value !== "1.5");

  const accountNumbers = useMemo(() => {
    const seen = new Set<string>();
    const nums: string[] = [];
    for (const p of portfolios) {
      for (const a of p.accounts ?? []) {
        if (a.tagsAsSet?.includes("KAR-OK") && !seen.has(a.number)) {
          seen.add(a.number);
          nums.push(a.number);
        }
      }
    }
    return nums;
  }, [portfolios]);

  const [priceListOpen, setPriceListOpen] = useState(false);

  return (
    <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto grid max-w-6xl items-start gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_300px]">
        <datalist id="of-securities">
          {securities.slice(0, 500).map((s) => (
            <option key={s.id} value={s.name} />
          ))}
        </datalist>

        <div className="min-w-0 divide-y divide-border/70 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          <div className="p-4 sm:p-5">
            <PortfolioSection
              portfolios={portfolios}
              isCompany={isCompany}
              selected={selected}
              isNew={isNew}
              isTransferAccount={isTransferAccount}
              onJumpToTransfer={scrollToTransferSection}
            />
          </div>
          <div className="p-4 sm:p-5">
            <StrategySection
              portType={portType}
              modelType={modelType}
              modelOptions={modelOptions}
              showMinWarn={showMinWarn}
              minThreshold={minThreshold}
              totalPortValue={totalPortValue}
            />
          </div>
          <div className="p-4 sm:p-5">
            <FeesSection
              feeOptions={feeOptions}
              modelIsSet={modelIsSet}
              priceListOpen={priceListOpen}
              onTogglePriceList={() => setPriceListOpen((v) => !v)}
            />
          </div>
          <div className="p-4 sm:p-5">
            <FinancingSection
              accountNumbers={accountNumbers}
              bankAccount={bankAccount}
              isNew={isNew}
              isCapguardPort={isCapguardPort}
              onCopyAmount={copyAmountToOneOff}
            />
          </div>
          {isNoModel && (
            <div className="p-4 sm:p-5">
              <ManualOrdersSection
                selectedPortfolio={selected}
                securities={securities}
              />
            </div>
          )}
          <div className="p-4 sm:p-5">
            <AgreementsSection
              securities={securities}
              accountNumbers={accountNumbers}
            />
          </div>
          {isTransferAccount && (
            <div id="of-transfer-section" className="p-4 sm:p-5">
              <TransferSection accountType={transferAccountType} />
            </div>
          )}
        </div>

        <aside className="min-w-0 space-y-3 xl:sticky xl:top-4">
          <SummaryCard portfolios={portfolios} modelOptions={modelOptions} />
          <ProjectionCard portfolios={portfolios} modelsData={modelsData} />
          <PriceListCard
            open={priceListOpen}
            onToggle={() => setPriceListOpen((v) => !v)}
            amountToInvest={amountToInvest}
            modelType={modelType}
            managementFee={managementFee}
          />
        </aside>
      </div>
    </div>
  );
}

// ─── Exported form ─────────────────────────────────────────────────────────────

export type OrderFormCleanProps = {
  portfolios: Portfolio[];
  isCompany?: boolean;
  securities?: QuerySecurity[];
  modelsData?: QueryModelPosition[];
  initialData?: Record<string, unknown>;
  documentId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDocumentCreated?: (data: Record<string, any>, documentId?: string) => void;
  onClose?: () => void;
};

export function OrderFormClean({
  portfolios,
  isCompany = false,
  securities = [],
  modelsData = [],
  initialData,
  documentId,
  onDocumentCreated,
  onClose,
}: OrderFormCleanProps) {
  const form = useOrderForm(initialData as Parameters<typeof useOrderForm>[0]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (data: Record<string, any>) => {
    const minThreshold = getMinimumMandateAmount(data.dpModelType);
    const totalPortValue =
      (Number(data.dpSumAvCash) || 0) +
      (Number(data.dpSumInsValue) || 0) +
      (Number(data.dpAmountToInvest) || 0);
    if (
      minThreshold > 0 &&
      totalPortValue > 0 &&
      totalPortValue < minThreshold
    ) {
      form.setError("dpAmountToInvest", {
        type: "manual",
        message: `${
          data.dpModelType === "CAPGUARDEKB" ? "Aktiv EKB" : "Aktiv Eiendom"
        } krever en minimumsverdi på ${fmt(minThreshold)} kr.`,
      });
      return;
    }
    form.clearErrors("dpAmountToInvest");

    if (data.dpModelType === "NOMODEL") {
      const buyRows = (data.buyOrders ?? []) as Array<{
        instrumentName?: string;
        securityId?: number | null;
        amountInput?: string;
      }>;
      const invalidBuyRows = buyRows.filter(
        (row) =>
          row.instrumentName &&
          !securities.some((security) => security.id === row.securityId)
      );
      if (invalidBuyRows.length > 0) {
        form.setError("buyOrders", {
          type: "manual",
          message: "Velg et fond eller instrument fra listen.",
        });
        return;
      }
      if (data.dpAmountToInvest > 0) {
        const sumBuy = buyRows
          .filter((r) => r.instrumentName)
          .reduce((s, r) => s + (parseFloat(r.amountInput ?? "") || 0), 0);
        if (Math.abs(sumBuy - data.dpAmountToInvest) > 0.01) {
          form.setError("buyOrders", {
            type: "manual",
            message: `Kjøpsordre totalt (${fmt(
              sumBuy
            )}) må tilsvare investeringsbeløpet (${fmt(
              data.dpAmountToInvest
            )})`,
          });
          return;
        }
      }
      form.clearErrors("buyOrders");
    }

    const selectedPortfolio = portfolios.find(
      (p) => p.id === Number(data.dpPortfolioId)
    );
    onDocumentCreated?.(
      {
        existData: buildExistData(selectedPortfolio),
        newData: { ...initialData, ...data },
      },
      documentId
    );
    onClose?.();
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex h-full min-h-0 flex-col"
      >
        <OrderFormCleanContent
          portfolios={portfolios}
          securities={securities}
          isCompany={isCompany}
          modelsData={modelsData}
        />
        <div className="z-10 flex shrink-0 justify-end gap-3 border-t border-border bg-background px-4 py-3">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
            >
              Avbryt
            </button>
          )}
          <button
            type="submit"
            className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
          >
            {documentId ? "Lagre endringer" : "Legg til dokument"}
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
